import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DattoAccessFeedbackAlert from "./DattoAccessFeedbackAlert";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Collapse,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  appConfigSchemas,
  availablePlatforms,
  availableApplications,
  ConfigField,
  m365DefaultConfig,
  sherwebDefaultConfig,
  itglueDefaultConfig,
  getPlatformConfigSchemaFields,
} from "../utils/constants";
import { formatAppName } from "../utils/connectorUtils";
import M365PreSaveInstructionsInner from "./m365/M365PreSaveInstructionsInner";
import M365PostSaveGrantPermissionsPanel from "./m365/M365PostSaveGrantPermissionsPanel";
import { useIntegration } from "../hooks/useIntegration";
import { useUserMsp } from "../hooks/useUserMsp";
import IntegrationApi from "../apis/integrationApi";
import LayeredAutocompleteSelect from "./LayeredAutocompleteSelect";
import { getDropdownSafeDialogSx } from "../utils/dialogLayering";
import { isBrowserUseLdEnabled } from "../utils/browserUseAccess";
import { normalizeOnePasswordSignInAddress, ONEPASSWORD_APP, ONEPASSWORD_BASE_URL_KEY } from "../utils/onePasswordConfig";
import { usePublishConfigurationSupport } from "../configurationSupport/ConfigurationSupportContext";
import { CollapsibleSetupGuidePanel } from "./integrationGuides/CollapsibleSetupGuidePanel";
import { HaloPsaGuideInner } from "./integrationGuides/HaloPsaGuideInner";
import { AutotaskPsaGuideInner } from "./integrationGuides/AutotaskPsaGuideInner";
import { ConnectWisePsaGuideInner } from "./integrationGuides/ConnectWisePsaGuideInner";
import { NinjaOneRmmGuideInner } from "./integrationGuides/NinjaOneRmmGuideInner";
import { DattoRmmGuideInner } from "./integrationGuides/DattoRmmGuideInner";
import { HuduSetupGuideSteps } from "./integrationGuides/HuduSetupGuideSteps";
import { ITGlueApiKeyGuideSteps } from "./integrationGuides/ITGlueApiKeyGuideSteps";
import { GWorkspaceManualStepInstructions } from "./integrationGuides/GWorkspaceManualStepInstructions";
import { GWorkspaceSetupGuideSteps } from "./integrationGuides/GWorkspaceSetupGuideSteps";
import { SherwebSetupGuideSteps } from "./integrationGuides/SherwebSetupGuideSteps";
import GWorkspaceConnectionChecksResultsBox from "./gworkspace/GWorkspaceConnectionChecksResultsBox";
import GWorkspaceOnboardingPanel, {
  isRecoverable as isOnboardingRecoverable,
  isTerminal as isOnboardingTerminal,
} from "./gworkspace/GWorkspaceOnboardingPanel";
import type { OnboardingRun } from "../apis/gworkspaceOnboardingApi";
import GWorkspaceOAuthScopeChecksResultsBox, {
  GWorkspaceOAuthScopeCheck,
} from "./gworkspace/GWorkspaceOAuthScopeChecksResultsBox";
import {
  applyGWorkspaceServiceAccountUpload,
  buildGWorkspaceConfig,
  GWORKSPACE_ADMIN_EMAIL_KEY,
  GWORKSPACE_JSON_UPLOAD_KEY,
  validateGWorkspaceForm,
} from "../utils/gworkspaceConfig";
import HaloPermissionFeedbackAlert from "./HaloPermissionFeedbackAlert";
import ItGlueAccessFeedbackAlert from "./ItGlueAccessFeedbackAlert";
import {
  HaloPermissionFeedback,
  fetchHaloPermissionFeedback,
} from "../utils/haloPermissionChecks";
import { buildItGlueAccessFeedback, ItGlueAccessFeedback } from "../utils/itGlueAccessChecks";
import { useNinjaOneRefreshToken } from "../hooks/useNinjaOneRefreshToken";

/** A customer a per-customer application (Microsoft 365, Slack, a vault) can be connected for. */
export interface IntegrationCustomerOption {
  id: string | number;
  name: string;
  /** Short suffix after the name, such as "your organization". */
  detail?: string;
}

interface AddIntegrationModalProps {
  open: boolean;
  mode: "platform" | "application";
  customerId?: string;
  customerName?: string;
  /**
   * Customers the technician can connect this application for. When provided on an
   * application dialog, a Customer field is shown and save/test use the selection.
   * Omit it on a surface that is already scoped to one customer.
   */
  customers?: IntegrationCustomerOption[];
  onClose: () => void;
  onSuccess: () => void;
  initialPlatformName?: string;
  initialPlatformType?: string;
  initialApplicationName?: string;
}

// A Google Workspace run that completed is tested at once and, when that look was not clean,
// once more this long after, by which time the Super Admin role it assigned has propagated (Q127).
const GWORKSPACE_COMPLETION_RETEST_DELAY_MS = 15_000;

const normalizeHuduBaseUrl = (raw: string): string => {
  let val = raw.trim();
  if (!val) return "";
  if (!val.startsWith("https://") && !val.startsWith("http://")) {
    val = `https://${val}`;
  }
  val = val.replace(/\/+$/, "");
  if (!val.endsWith("/api/v1")) {
    val = `${val}/api/v1`;
  }
  return val;
};

/**
 * The two ways a Google Workspace tenant gets set up, offered side by side so the path that
 * existed before Theo could drive a console keeps working (Q164). Manual is where the dialog
 * opens: Auto is the newer path and the technician switches to it deliberately, until it is the
 * one worth defaulting to.
 */
const MANUAL_TAB = "manual";
const AUTO_TAB = "auto";
type GWorkspaceTab = typeof MANUAL_TAB | typeof AUTO_TAB;

const GWORKSPACE_TAB_LABEL: Record<GWorkspaceTab, string> = {
  [MANUAL_TAB]: "Set up by hand",
  [AUTO_TAB]: "Let Theo set it up",
};

/**
 * Why a tab cannot be reached. Each names its cause and stops: the by-hand side has no way back
 * once [Start] has locked (Q169), and the Auto side keeps the same shape rather than growing the
 * one extra sentence its opposite number could never have (Q172).
 */
const AUTO_TAB_BLOCKED = "A key has been added, so Auto is blocked.";
const MANUAL_TAB_BLOCKED = "Theo is setting this up. Manual setup is blocked.";

const customerOptionLabel = (customer: IntegrationCustomerOption): string =>
  customer.detail ? `${customer.name} (${customer.detail})` : customer.name;

const AddIntegrationModal: React.FC<AddIntegrationModalProps> = ({
  open,
  mode,
  customerId: initialCustomerId,
  customerName: initialCustomerName,
  customers,
  onClose,
  onSuccess,
  initialPlatformName,
  initialPlatformType,
  initialApplicationName,
}) => {
  const { createPlatform, createApplication, getAvailablePlatforms, getAvailableApplications } = useIntegration();
  const integrationApi = useMemo(() => new IntegrationApi(), []);
  const { userMspInfo } = useUserMsp();

  const [selectedPlatform, setSelectedPlatform] = useState<string>("");
  const [selectedPlatformType, setSelectedPlatformType] = useState<string>("");
  const [selectedApplication, setSelectedApplication] = useState<string>("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(initialCustomerId || "");
  const [configValues, setConfigValues] = useState<Record<string, string>>({});
  const [fileNames, setFileNames] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    has_warnings?: boolean;
    message: string;
    checks?: Array<{ id: string; label: string; status: string; detail?: string }>;
    scope_checks?: GWorkspaceOAuthScopeCheck[];
  } | null>(null);
  const [haloPermissionFeedback, setHaloPermissionFeedback] =
    useState<HaloPermissionFeedback | null>(null);
  const [itGlueAccessFeedback, setItGlueAccessFeedback] = useState<ItGlueAccessFeedback | null>(null);
  const [testSucceeded, setTestSucceeded] = useState(false);
  const [availablePlatformsList, setAvailablePlatformsList] = useState<any[]>([]);
  const [availableApplicationsList, setAvailableApplicationsList] = useState<any[]>([]);
  const [showM365Instructions, setShowM365Instructions] = useState(false);
  const [huduInstructionsExpanded, setHuduInstructionsExpanded] = useState(false);
  const [autotaskInstructionsExpanded, setAutotaskInstructionsExpanded] = useState(false);
  /** Expandable Halo PSA setup guide (URL, API agent, API application) — matches Hudu pattern in this modal */
  const [haloInstructionsExpanded, setHaloInstructionsExpanded] = useState(false);
  const [connectWiseInstructionsExpanded, setConnectWiseInstructionsExpanded] = useState(false);
  const [ninjaOneInstructionsExpanded, setNinjaOneInstructionsExpanded] = useState(false);
  const [dattoInstructionsExpanded, setDattoInstructionsExpanded] = useState(false);
  const [itglueInstructionsExpanded, setItglueInstructionsExpanded] = useState(false);
  const [gworkspaceInstructionsExpanded, setGworkspaceInstructionsExpanded] = useState(false);
  /**
   * Whether browser automation is switched on for whoever is looking. Read straight from the
   * flags this app already holds: `browser-use` is targeted on the `user` context the provider
   * sends, matched by its `mspName` attribute (Q163).
   */
  /**
   * Which way this dialog is setting Workspace up. Manual until something says otherwise: a run
   * that exists moves it to Auto, because that is where the run is (Q171).
   */
  const [gworkspaceTab, setGworkspaceTab] = useState<GWorkspaceTab>(MANUAL_TAB);
  /** Once the technician has picked a tab, nothing picks for them again. */
  const gworkspaceTabPicked = useRef(false);
  const ldFlags = useFlags();
  const browserUseEnabled = isBrowserUseLdEnabled(ldFlags as Record<string, unknown>);
  /**
   * Google Workspace is set up by Theo driving the consoles, started from [Start] here. The
   * newest run decides the rest (Q132): [Start] is dead once a run exists; a failed run brings
   * the manual instructions for its undone steps, the JSON field if no key was saved, and a
   * Test-gated [Save] (Q95, Q131).
   */
  /** Bumped by [Start]; the panel opens a sign-in for each new value. */
  const [onboardingStartSignal, setOnboardingStartSignal] = useState(0);
  /** [Start] was pressed and the sign-in is in progress; cleared if it is cancelled before job 1 (Q134). */
  const [onboardingActive, setOnboardingActive] = useState(false);
  /** The run wrote application_config itself, so the integration exists without [Save]. */
  const [onboardingSavedConfig, setOnboardingSavedConfig] = useState(false);
  const [onboardingTesting, setOnboardingTesting] = useState(false);
  const [onboardingRun, setOnboardingRun] = useState<OnboardingRun | null>(null);
  /** The licence checkbox under Super Admin Email, on by default (Q145, Q161). Read by the panel at [I'm done]. */
  const [onboardingAssignLicense, setOnboardingAssignLicense] = useState(true);
  const onboardingAutoTest = useRef<number | null>(null);

  /**
   * What the panel reads back, and the address it is running as.
   *
   * The typed address is browser state and closing the dialog throws it away, but the run
   * carries its own copy in the ledger and that is the one every step uses. A dialog reopened
   * mid-run would otherwise show an empty box under a heading naming the address, so the run's
   * copy fills it. Never over a value the technician is typing.
   *
   * Stable identity: the panel polls on an effect keyed by this callback.
   */
  const handleOnboardingRunChange = useCallback((run: OnboardingRun | null) => {
    setOnboardingRun(run);
    const adminEmail = run?.admin_email;
    if (!adminEmail) return;
    setConfigValues((current) =>
      current[GWORKSPACE_ADMIN_EMAIL_KEY]?.trim() ? current : { ...current, [GWORKSPACE_ADMIN_EMAIL_KEY]: adminEmail }
    );
  }, []);
  const [sherwebInstructionsExpanded, setSherwebInstructionsExpanded] = useState(false);
  const [m365SaveSuccess, setM365SaveSuccess] = useState(false);
  const showCustomerSelector = mode === "application" && customers != null;
  const effectiveCustomerId = showCustomerSelector ? selectedCustomerId : initialCustomerId || "";
  const effectiveCustomerName =
    (customers || []).find((customer) => String(customer.id) === String(effectiveCustomerId))?.name ||
    initialCustomerName ||
    "";
  const { isGettingRefreshToken: ninjaOneRefreshTokenLoading, startSignIn: handleStartNinjaOneSignIn } = useNinjaOneRefreshToken({
    instanceUrl: configValues.instance_url || "",
    clientId: configValues.client_id || "",
    clientSecret: configValues.client_secret || "",
    enabled: open && mode === "platform" && selectedPlatform.toLowerCase() === "ninjaone",
    contextKey: `add:${selectedPlatformType}:${selectedPlatform}:${effectiveCustomerId || ""}`,
    onStart: () => {
      setTestResult(null);
      setTestSucceeded(false);
    },
    onSuccess: (token) => {
      setConfigValues((previous) => ({ ...previous, refresh_token: token }));
      setErrors((previous) => {
        const next = { ...previous };
        delete next.refresh_token;
        return next;
      });
      setTestResult({ success: true, message: "Refresh Token added. Click Test to verify NinjaOne." });
    },
    onError: (message) => setTestResult({ success: false, message }),
  });

  useEffect(() => {
    if (open) {
      if (mode === "platform") {
        loadAvailablePlatforms();
      } else {
        loadAvailableApplications();
      }
      resetForm();
      if (mode === "platform") {
        if (initialPlatformType) setSelectedPlatformType(initialPlatformType);
        if (initialPlatformName) {
          setSelectedPlatform(initialPlatformName);
          const key = String(initialPlatformName || "").toLowerCase();
          if (key === "itglue") {
            setConfigValues({ ...itglueDefaultConfig });
          } else if (key === "sherweb") {
            setConfigValues({ ...sherwebDefaultConfig });
          }
        }
      } else if (initialApplicationName) {
        setSelectedApplication(initialApplicationName);
        if (initialApplicationName.toLowerCase() === "m365") {
          setConfigValues({ ...m365DefaultConfig });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const loadAvailablePlatforms = async () => {
    try {
      const response = await getAvailablePlatforms();
      setAvailablePlatformsList(response?.rows || availablePlatforms);
    } catch {
      setAvailablePlatformsList(availablePlatforms);
    }
  };

  const loadAvailableApplications = async () => {
    try {
      const response = await getAvailableApplications();
      setAvailableApplicationsList(response?.rows || availableApplications);
    } catch {
      setAvailableApplicationsList(availableApplications);
    }
  };

  const resetForm = () => {
    setSelectedPlatform("");
    setSelectedPlatformType("");
    setSelectedApplication("");
    setSelectedCustomerId(initialCustomerId || "");
    setConfigValues({});
    setFileNames({});
    setErrors({});
    setTestResult(null);
    setHaloPermissionFeedback(null);
    setItGlueAccessFeedback(null);
    setTestSucceeded(false);
    setM365SaveSuccess(false);
    setGworkspaceInstructionsExpanded(false);
    setOnboardingStartSignal(0);
    setOnboardingActive(false);
    setGworkspaceTab(MANUAL_TAB);
    gworkspaceTabPicked.current = false;
    setOnboardingSavedConfig(false);
    setOnboardingTesting(false);
    setOnboardingRun(null);
    if (onboardingAutoTest.current != null) {
      window.clearTimeout(onboardingAutoTest.current);
      onboardingAutoTest.current = null;
    }
    setHuduInstructionsExpanded(false);
    setAutotaskInstructionsExpanded(false);
    setHaloInstructionsExpanded(false);
    setConnectWiseInstructionsExpanded(false);
    setNinjaOneInstructionsExpanded(false);
    setDattoInstructionsExpanded(false);
    setItglueInstructionsExpanded(false);
    setSherwebInstructionsExpanded(false);
  };

  const getReadableErrorMessage = (
    error: any,
    fallback: string
  ): string => {
    const details = error?.response?.data?.details;
    const apiError = error?.response?.data?.error;
    const userMessage = error?.userMessage;
    const message = error?.message;

    if (typeof details === "string" && details.trim()) {
      return details;
    }
    if (typeof apiError === "string" && apiError.trim()) {
      return apiError;
    }
    if (typeof userMessage === "string" && userMessage.trim()) {
      return userMessage;
    }
    if (typeof message === "string" && message.trim()) {
      return message;
    }
    return fallback;
  };

  const getConfigSchema = (): ConfigField[] => {
    if (mode === "platform") {
      if (!selectedPlatform) return [];
      return getPlatformConfigSchemaFields(selectedPlatform);
    } else {
      if (!selectedApplication) return [];
      return appConfigSchemas[selectedApplication.toLowerCase()] || [];
    }
  };

  const handleFieldChange = (key: string, value: string) => {
    setConfigValues((prev) => ({
      ...prev,
      [key]: value,
    }));
    if (errors[key]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
    setTestSucceeded(false);
    setTestResult(null);
    setHaloPermissionFeedback(null);
    setItGlueAccessFeedback(null);
  };

  const handleGWorkspaceServiceAccountUpload = (rawJson: string, fileName: string) => {
    try {
      const { values, fileNames: uploadedFileNames } = applyGWorkspaceServiceAccountUpload(
        rawJson,
        fileName
      );
      setConfigValues((prev) => ({ ...prev, ...values }));
      setFileNames((prev) => ({ ...prev, ...uploadedFileNames }));
      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors[GWORKSPACE_JSON_UPLOAD_KEY];
        return nextErrors;
      });
      setTestSucceeded(false);
      setTestResult({
        success: true,
        message: "Service account JSON loaded.",
      });
      setHaloPermissionFeedback(null);
    } catch (error: any) {
      setTestSucceeded(false);
      setTestResult({
        success: false,
        message:
          error?.message ||
          "Invalid service account JSON file. Please upload a valid JSON object.",
      });
      setHaloPermissionFeedback(null);
    }
  };

  const handleFileChange = (
    key: string,
    file: File | null,
    options?: { readMode?: "base64" | "text" }
  ) => {
    if (!file) {
      handleFieldChange(key, "");
      setFileNames((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const value =
        options?.readMode === "text"
          ? String(reader.result || "")
          : ((reader.result as string)?.split(",")[1] ?? "");

      if (
        selectedApplication === "gworkspace" &&
        key === GWORKSPACE_JSON_UPLOAD_KEY &&
        options?.readMode === "text"
      ) {
        handleGWorkspaceServiceAccountUpload(value, file.name);
        return;
      }

      handleFieldChange(key, value);
      setFileNames((prev) => ({ ...prev, [key]: file.name }));
    };
    if (options?.readMode === "text") {
      reader.readAsText(file, "utf-8");
    } else {
      reader.readAsDataURL(file);
    }
  };

  const handleCustomerChange = (nextCustomerId: string) => {
    setSelectedCustomerId(nextCustomerId);
    setErrors((prev) => {
      if (!prev.customer) return prev;
      const next = { ...prev };
      delete next.customer;
      return next;
    });
    setTestSucceeded(false);
    setTestResult(null);
    setHaloPermissionFeedback(null);
    setItGlueAccessFeedback(null);
    // A different customer has its own Workspace run. Drop the one we were showing so the
    // panel's next status read is what decides the tab.
    setOnboardingStartSignal(0);
    setOnboardingActive(false);
    setOnboardingSavedConfig(false);
    setOnboardingRun(null);
    setGworkspaceTab(MANUAL_TAB);
    gworkspaceTabPicked.current = false;
  };

  const validateForm = (): boolean => {
    const schema = getConfigSchema();
    const newErrors: Record<string, string> = {};

    if (showCustomerSelector && !String(selectedCustomerId || "").trim()) {
      setErrors((prev) => ({ ...prev, customer: "Customer is required" }));
      return false;
    }

    if (selectedApplication === "gworkspace") {
      // The guided run produces the JSON itself, so only the address is asked for up front; the
      // key is required only when the run failed before it could save one.
      const gworkspaceErrors = gworkspaceJsonRequired
        ? validateGWorkspaceForm(configValues)
        : configValues[GWORKSPACE_ADMIN_EMAIL_KEY]?.trim()
          ? {}
          : { [GWORKSPACE_ADMIN_EMAIL_KEY]: "Super Admin Email is required." };
      setErrors(gworkspaceErrors);
      return Object.keys(gworkspaceErrors).length === 0;
    }

    if (mode === "application" && selectedApplication === ONEPASSWORD_APP) {
      const address = normalizeOnePasswordSignInAddress(configValues[ONEPASSWORD_BASE_URL_KEY] || "");
      const onePasswordErrors: Record<string, string> = address.ok ? {} : { [ONEPASSWORD_BASE_URL_KEY]: address.error };
      setErrors(onePasswordErrors);
      return address.ok;
    }

    schema.forEach((field) => {
      if (field.required && !configValues[field.key]) {
        newErrors[field.key] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const applyHuduNormalization = () => {
    if (selectedPlatform?.toLowerCase() === "hudu") {
      const normalized = normalizeHuduBaseUrl(configValues.base_url || "");
      if (normalized && normalized !== configValues.base_url) {
        setConfigValues((prev) => ({ ...prev, base_url: normalized }));
        return { ...configValues, base_url: normalized };
      }
    }
    return configValues;
  };

  const handleTest = async () => {
    if (!validateForm()) {
      return;
    }

    setTesting(true);
    setTestResult(null);
    setHaloPermissionFeedback(null);
    setItGlueAccessFeedback(null);
    setTestSucceeded(false);

    const normalizedConfig = applyHuduNormalization();

    try {
      if (mode === "platform") {
        const result = await integrationApi.testPlatformConfigurationDirect(selectedPlatform, normalizedConfig);
        setTestResult({
          success: result.success,
          has_warnings: result.has_warnings,
          message: result.message || "Connection successful",
          checks: result.checks,
          scope_checks: result.scope_checks,
        });
        const plat = selectedPlatform?.toLowerCase() || "";
        if (plat === "halo") {
          const haloConfig = normalizedConfig as Record<string, string>;
          const permissionFeedback = await fetchHaloPermissionFeedback({
              url: String(haloConfig.url || "").trim(),
              client_id: String(haloConfig.client_id || "").trim(),
              client_secret: String(haloConfig.client_secret || "").trim(),
            });
          setHaloPermissionFeedback(permissionFeedback);
        } else if (plat === "itglue" && result.details?.permission_checks?.length) {
          setItGlueAccessFeedback(buildItGlueAccessFeedback(result));
        }
        setTestSucceeded(result.success === true);
      } else {
        let configToTest: Record<string, unknown> = { ...normalizedConfig };
        
        if (selectedApplication === "gworkspace") {
          configToTest = buildGWorkspaceConfig(configValues);
        }

        const result = await integrationApi.testApplicationConfigurationDirect(
          selectedApplication,
          configToTest,
          selectedApplication?.toLowerCase() === "m365"
            ? { credentialsOnly: true }
            : undefined
        );
        setTestResult({
          success: result.success,
          has_warnings: result.has_warnings,
          message: result.message || "Connection successful",
          checks: result.checks,
          scope_checks: result.scope_checks,
        });
        setHaloPermissionFeedback(null);
        setItGlueAccessFeedback(null);
        setTestSucceeded(result.success === true);
      }
    } catch (error: any) {
      const errorChecks = Array.isArray(error?.response?.data?.checks)
        ? error.response.data.checks
        : undefined;
      const errorScopeChecks = Array.isArray(error?.response?.data?.scope_checks)
        ? error.response.data.scope_checks
        : undefined;
      setTestResult({
        success: false,
        message: getReadableErrorMessage(error, "Connection test failed"),
        checks: errorChecks,
        scope_checks: errorScopeChecks,
      });
      setHaloPermissionFeedback(null);
      setItGlueAccessFeedback(null);
      setTestSucceeded(false);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    const normalizedConfig = applyHuduNormalization();

    try {
      if (mode === "platform") {
        if (!userMspInfo?.msp_id) {
          throw new Error("MSP ID not found");
        }
        await createPlatform(userMspInfo.msp_id, {
          type: selectedPlatformType,
          name: selectedPlatform,
          config_json: normalizedConfig,
        });
      } else {
        if (effectiveCustomerId) {
          // After a run that saved the key itself, [Save] carries only the address; the server
          // keeps the stored key (Q131).
          const signInAddress = normalizeOnePasswordSignInAddress(configValues[ONEPASSWORD_BASE_URL_KEY] || "");
          const configToSave =
            selectedApplication === "gworkspace"
              ? gworkspaceJsonRequired
                ? buildGWorkspaceConfig(configValues)
                : { admin_email: configValues[GWORKSPACE_ADMIN_EMAIL_KEY] || "" }
              : selectedApplication === ONEPASSWORD_APP && signInAddress.ok
                ? { [ONEPASSWORD_BASE_URL_KEY]: signInAddress.value }
                : (configValues as Record<string, unknown>);

          await integrationApi.addApplicationToCustomerByName(
            effectiveCustomerId,
            selectedApplication,
            configToSave
          );
        } else {
          await createApplication({
            name: selectedApplication,
            available_actions: [],
          });
        }
      }
      onSuccess();
      if (mode === "application" && selectedApplication?.toLowerCase() === "m365" && effectiveCustomerId) {
        setM365SaveSuccess(true);
      } else {
        onClose();
        resetForm();
      }
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || "Failed to create integration",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleM365Done = () => {
    setM365SaveSuccess(false);
    resetForm();
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const configSchema = getConfigSchema();
  const isGWorkspace =
    mode === "application" && selectedApplication?.toLowerCase() === "gworkspace";
  // 1Password has nothing to test the sign-in address against, so it saves without a Test.
  const isOnePassword = mode === "application" && selectedApplication === ONEPASSWORD_APP;
  /**
   * Theo drives the consoles -- but only where browser automation is switched on. With the flag
   * off this is the dialog as it was before Theo could drive anything: the technician's own
   * service-account JSON, a Test and a Save (Q163).
   */
  /** Both ways of setting Workspace up are offered, as two tabs, only where the flag is on (Q164). */
  const gworkspaceTabsShown = isGWorkspace && Boolean(effectiveCustomerId) && browserUseEnabled;
  const gworkspaceGuided = gworkspaceTabsShown && gworkspaceTab === AUTO_TAB;
  const gworkspaceManual = isGWorkspace && !gworkspaceGuided;
  // Read off the run rather than off the tab: the tab a technician is looking at must not change
  // what is true of their run, because the other tab's lock is decided from exactly these.
  const onboardingRunning = Boolean(gworkspaceTabsShown && onboardingRun && !isOnboardingTerminal(onboardingRun));
  /**
   * A failed run whose address was refused (Q162): nothing was created, so the dialog stays as it
   * was before Start -- the address editable, [Start] live, no instructions to finish by hand.
   */
  const onboardingRecoverable = Boolean(gworkspaceTabsShown && isOnboardingRecoverable(onboardingRun));
  /** A failed run the technician has not finished by hand yet: instructions, maybe the JSON field, [Save]. */
  const onboardingFailedOpen = Boolean(
    gworkspaceGuided && onboardingRun?.status === "failed" && !onboardingRun?.manually_completed_at && !onboardingRecoverable
  );
  /**
   * [Start] is spent: pressed and in flight, or a run exists that another press cannot help. The
   * one condition, because the by-hand tab locks exactly when [Start] does and not a moment
   * apart (Q169) -- a recoverable failure gives both of them back together.
   */
  const startLocked = onboardingActive || (onboardingRun != null && !onboardingRecoverable);
  /** A key in the form is a decision to do this by hand, taken the moment the file lands (Q167). */
  const gworkspaceKeyUploaded = Boolean(configValues[GWORKSPACE_JSON_UPLOAD_KEY]);
  /** Why a tab cannot be reached, or "" when it can. Clearing the key opens Auto again (Q168). */
  const gworkspaceTabBlocked = (tab: GWorkspaceTab): string => {
    if (tab === AUTO_TAB && gworkspaceKeyUploaded) return AUTO_TAB_BLOCKED;
    if (tab === MANUAL_TAB && startLocked) return MANUAL_TAB_BLOCKED;
    return "";
  };
  /** The run saved a key at its key step; Test reads it from the database rather than the form. */
  const onboardingKeySaved = Boolean(onboardingSavedConfig || onboardingRun?.configuration_saved);
  /** The JSON field, and the JSON in Save's payload: only when a failed run saved no key (Q95, Q138). */
  const gworkspaceJsonRequired = gworkspaceManual || (onboardingFailedOpen && !onboardingKeySaved);
  const onboardingFailedSteps = onboardingFailedOpen ? (onboardingRun?.steps ?? []).filter((step) => step.state !== "done").map((step) => step.name) : [];

  useEffect(() => {
    if (onboardingFailedOpen) setGworkspaceInstructionsExpanded(true);
  }, [onboardingFailedOpen]);

  /**
   * A customer with a run is shown the tab that run is on, whatever state it is in: going,
   * finished, or failed (Q171). It is also what a technician who closed the dialog mid-run comes
   * back to. Their own choice always wins over this.
   */
  useEffect(() => {
    if (!gworkspaceTabsShown || gworkspaceTabPicked.current || onboardingRun == null) return;
    setGworkspaceTab(AUTO_TAB);
  }, [gworkspaceTabsShown, onboardingRun]);

  /**
   * One test against what the run saved, reading the key from the database rather than the form.
   * Answers whether everything came back clean, which is what decides a second look.
   */
  const runSavedGWorkspaceTest = async (): Promise<boolean> => {
    if (!effectiveCustomerId) return false;
    setTestResult(null);
    try {
      const result = await integrationApi.testApplicationConfiguration(effectiveCustomerId, selectedApplication, {
        admin_email: configValues[GWORKSPACE_ADMIN_EMAIL_KEY] || "",
      });
      setTestResult({
        success: result.success,
        has_warnings: result.has_warnings,
        message: result.message || "Connection successful",
        checks: result.checks,
        scope_checks: result.scope_checks,
      });
      setTestSucceeded(result.success !== false);
      return result.success !== false && !result.has_warnings;
    } catch (error: any) {
      setTestResult({ success: false, message: getReadableErrorMessage(error, "Connection test failed.") });
      setTestSucceeded(false);
      return false;
    }
  };

  /** The [Test] button on the guided path. */
  const handleTestSavedGWorkspace = async () => {
    if (!effectiveCustomerId) return;
    setOnboardingTesting(true);
    try {
      await runSavedGWorkspaceTest();
    } finally {
      setOnboardingTesting(false);
    }
  };

  const handleStartOnboarding = () => {
    if (!validateForm()) return;
    setTestResult(null);
    setOnboardingActive(true);
    setOnboardingStartSignal((n) => n + 1);
  };

  /**
   * All nine steps went green. The key step wrote the configuration, so the integration already
   * exists and the existing test runs against what was saved (Q120).
   *
   * It runs at once, and again shortly after if anything came back short of clean. The first
   * look is the one the technician is waiting for; the second covers the Super Admin role still
   * propagating, which is the one failure that waiting actually fixes (Q127). The button stays
   * busy across both, because a still button with work pending is what reads as nothing happening.
   *
   * A failed run runs nothing: the technician tests when they have finished it by hand.
   */
  const handleOnboardingFinished = (run: OnboardingRun) => {
    setOnboardingActive(false);
    if (run.status !== "completed" || !run.configuration_saved || !effectiveCustomerId) return;
    if (onboardingAutoTest.current != null) window.clearTimeout(onboardingAutoTest.current);
    setOnboardingTesting(true);
    void runSavedGWorkspaceTest()
      .then((clean) => {
        if (clean) {
          setOnboardingTesting(false);
          return;
        }
        onboardingAutoTest.current = window.setTimeout(() => {
          onboardingAutoTest.current = null;
          void runSavedGWorkspaceTest().finally(() => setOnboardingTesting(false));
        }, GWORKSPACE_COMPLETION_RETEST_DELAY_MS);
      })
      .catch(() => setOnboardingTesting(false));
  };
  useEffect(
    () => () => {
      if (onboardingAutoTest.current != null) window.clearTimeout(onboardingAutoTest.current);
    },
    []
  );
  const visibleConfigFields =
    isGWorkspace && !gworkspaceJsonRequired
      ? configSchema.filter((field) => field.key !== GWORKSPACE_JSON_UPLOAD_KEY)
      : configSchema;
  const dialogTitle = m365SaveSuccess
    ? "Grant API Permissions"
    : mode === "application" && effectiveCustomerName
      ? `Add New Application Integration for ${effectiveCustomerName}`
      : `Add New ${mode === "platform" ? "Platform" : "Application"} Integration`;

  const supportSnapshot = useMemo(() => {
    if (!open) return null;
    const selectedName = mode === "platform" ? selectedPlatform : selectedApplication;
    return {
      surface: "integrations" as const,
      title: selectedName
        ? `Adding ${selectedName}${effectiveCustomerName ? ` for ${effectiveCustomerName}` : ""}`
        : dialogTitle,
      configuration: {
        mode,
        customer_id: effectiveCustomerId || "",
        customer_name: effectiveCustomerName || "",
        platform: selectedPlatform,
        platform_type: selectedPlatformType,
        application: selectedApplication,
        fields: configValues,
        uploaded_file_names: fileNames,
        validation_errors: errors,
        test_result: testResult
          ? {
              success: testResult.success,
              has_warnings: Boolean(testResult.has_warnings),
              message: testResult.message,
              checks: (testResult.checks || []).map((check) => ({
                id: check.id,
                label: check.label,
                status: check.status,
                detail: check.detail || "",
              })),
            }
          : null,
        test_succeeded: testSucceeded,
        testing,
        saving: loading,
        gworkspace_setup: isGWorkspace ? (gworkspaceGuided ? "auto" : "manual") : "",
        gworkspace_key_uploaded: gworkspaceKeyUploaded,
        gworkspace_onboarding: onboardingRun
          ? {
              status: onboardingRun.status,
              current_step: onboardingRun.current_step,
              configuration_saved: onboardingRun.configuration_saved,
              failure_reason: onboardingRun.failure?.reason || "",
              failure_message: onboardingRun.failure?.message || "",
            }
          : null,
        halo_permission_feedback: haloPermissionFeedback,
        itglue_access_feedback: itGlueAccessFeedback,
      },
    };
  }, [
    open,
    mode,
    selectedPlatform,
    selectedApplication,
    effectiveCustomerName,
    dialogTitle,
    effectiveCustomerId,
    selectedPlatformType,
    configValues,
    fileNames,
    errors,
    testResult,
    testSucceeded,
    testing,
    loading,
    isGWorkspace,
    gworkspaceGuided,
    gworkspaceKeyUploaded,
    onboardingRun,
    haloPermissionFeedback,
    itGlueAccessFeedback,
  ]);
  usePublishConfigurationSupport("add-integration", supportSnapshot, 40);

  return (
    <Dialog
      open={open}
      onClose={m365SaveSuccess ? handleM365Done : handleClose}
      maxWidth="md"
      fullWidth
      sx={getDropdownSafeDialogSx({
        maxHeight: "calc(100vh - 48px)",
        display: "flex",
        flexDirection: "column",
      })}
    >
      <DialogTitle sx={{ fontFamily: 'var(--theo-font-sans)', fontWeight: 600, flexShrink: 0 }}>
        {dialogTitle}
      </DialogTitle>
      <DialogContent
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          flex: "1 1 auto",
          minHeight: 0,
        }}
      >
        <Box sx={{ pt: 2 }}>
          {m365SaveSuccess && effectiveCustomerId ? (
            <M365PostSaveGrantPermissionsPanel customerId={effectiveCustomerId} />
          ) : mode === "platform" ? (
            <>
              <LayeredAutocompleteSelect
                label="Type"
                value={selectedPlatformType}
                onChange={(nextValue) => {
                  setSelectedPlatformType(nextValue);
                  setSelectedPlatform("");
                  setConfigValues({});
                  setErrors({});
                  setTestSucceeded(false);
                  setTestResult(null);
                  setHuduInstructionsExpanded(false);
                  setAutotaskInstructionsExpanded(false);
                  setHaloInstructionsExpanded(false);
                  setConnectWiseInstructionsExpanded(false);
                  setNinjaOneInstructionsExpanded(false);
                  setDattoInstructionsExpanded(false);
                  setItglueInstructionsExpanded(false);
                  setSherwebInstructionsExpanded(false);
                }}
                options={Array.from(new Set(availablePlatformsList.flatMap((p) => p.types || []))).map(
                  (type: string) => ({
                    value: type,
                    label: type.toUpperCase(),
                  })
                )}
                noOptionsText="No platform types available"
                disableClearable
                textFieldSx={{ mb: 2 }}
              />

              {selectedPlatformType && (
                <LayeredAutocompleteSelect
                  label="Platform"
                  value={selectedPlatform}
                  onChange={(nextValue) => {
                    setSelectedPlatform(nextValue);
                    const key = String(nextValue || "").toLowerCase();
                    setConfigValues(
                      key === "itglue"
                        ? { ...itglueDefaultConfig }
                        : key === "sherweb"
                          ? { ...sherwebDefaultConfig }
                          : {}
                    );
                    setErrors({});
                    setTestSucceeded(false);
                    setTestResult(null);
                    setHuduInstructionsExpanded(false);
                    setAutotaskInstructionsExpanded(false);
                    setHaloInstructionsExpanded(false);
                    setConnectWiseInstructionsExpanded(false);
                    setNinjaOneInstructionsExpanded(false);
                    setDattoInstructionsExpanded(false);
                    setItglueInstructionsExpanded(false);
                    setSherwebInstructionsExpanded(false);
                  }}
                  options={availablePlatformsList
                    .filter((platform) => (platform.types || []).includes(selectedPlatformType))
                    .map((platform) => ({
                      value: platform.name,
                      label: formatAppName(platform.name),
                    }))}
                  noOptionsText="No platforms available"
                  disableClearable
                  textFieldSx={{ mb: 2 }}
                />
              )}
            </>
          ) : (
            <>
            {showCustomerSelector && (
              <Box sx={{ mb: 2 }}>
                <LayeredAutocompleteSelect
                  label="Customer"
                  value={selectedCustomerId}
                  onChange={handleCustomerChange}
                  options={(customers || []).map((customer) => ({
                    value: String(customer.id),
                    label: customerOptionLabel(customer),
                  }))}
                  required
                  matchFrom="any"
                  disableClearable
                  disabled={
                    loading ||
                    testing ||
                    onboardingTesting ||
                    onboardingActive ||
                    (onboardingRun != null && !isOnboardingTerminal(onboardingRun))
                  }
                  noOptionsText="No customers yet"
                  error={Boolean(errors.customer)}
                  helperText={errors.customer}
                />
              </Box>
            )}
            <LayeredAutocompleteSelect
              label="Application"
              value={selectedApplication}
              onChange={(app) => {
                setSelectedApplication(app);
                const appKey = app.toLowerCase();
                setConfigValues(
                  appKey === "m365"
                    ? { ...m365DefaultConfig }
                    : {}
                );
                setFileNames({});
                setErrors({});
                setTestSucceeded(false);
                setTestResult(null);
                setGworkspaceInstructionsExpanded(false);
                setSherwebInstructionsExpanded(false);
                setNinjaOneInstructionsExpanded(false);
              }}
              // 1Password is only run in a browser, so it is offered only where the browser-use flag is on.
              options={availableApplicationsList
                .filter((app) => app.name !== ONEPASSWORD_APP || browserUseEnabled)
                .map((app) => ({
                  value: app.name,
                  label: formatAppName(app.name),
                }))}
              noOptionsText="No applications available"
              disableClearable
              textFieldSx={{ mb: 2 }}
            />
            </>
          )}

          {configSchema.length > 0 && !m365SaveSuccess && (
            <Box sx={{ mt: 2 }}>
              {selectedApplication?.toLowerCase() === "m365" && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    fullWidth
                    onClick={() => setShowM365Instructions((v) => !v)}
                    sx={{
                      fontFamily: "var(--theo-font-sans)",
                      textTransform: "none",
                      justifyContent: "space-between",
                      color: "#705d4a",
                      border: "1px solid #e5e5e5",
                      "&:hover": { bgcolor: "#f9f9f9", borderColor: "#705d4a" },
                    }}
                  >
                    How to get Tenant ID, Client ID, Client Secret & Upload Certificate
                    {showM365Instructions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Button>
                  <Collapse in={showM365Instructions}>
                    <M365PreSaveInstructionsInner />
                  </Collapse>
                </Box>
              )}
              {selectedPlatform?.toLowerCase() === "hudu" && (
                <CollapsibleSetupGuidePanel
                  contentId="hudu-instructions-content"
                  expanded={huduInstructionsExpanded}
                  onExpandedChange={setHuduInstructionsExpanded}
                >
                  <HuduSetupGuideSteps />
                </CollapsibleSetupGuidePanel>
              )}
              {selectedPlatform?.toLowerCase() === "autotask" && (
                <CollapsibleSetupGuidePanel
                  contentId="autotask-instructions-content"
                  expanded={autotaskInstructionsExpanded}
                  onExpandedChange={setAutotaskInstructionsExpanded}
                >
                  <AutotaskPsaGuideInner />
                </CollapsibleSetupGuidePanel>
              )}
              {selectedPlatform?.toLowerCase() === "itglue" && (
                <CollapsibleSetupGuidePanel
                  contentId="itglue-instructions-content"
                  expanded={itglueInstructionsExpanded}
                  onExpandedChange={setItglueInstructionsExpanded}
                >
                  <Typography
                    sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 15, color: "#1a1a1a", mb: 1.5 }}
                  >
                    Setup Guide
                  </Typography>
                  <ITGlueApiKeyGuideSteps showTitle={false} listSx={{ "& li": { mb: 1, lineHeight: 1.6 } }} />
                </CollapsibleSetupGuidePanel>
              )}
              {selectedPlatform?.toLowerCase() === "halo" && (
                <CollapsibleSetupGuidePanel
                  contentId="halo-instructions-content"
                  expanded={haloInstructionsExpanded}
                  onExpandedChange={setHaloInstructionsExpanded}
                >
                  <HaloPsaGuideInner />
                </CollapsibleSetupGuidePanel>
              )}
              {selectedPlatform?.toLowerCase() === "connectwise" && (
                <CollapsibleSetupGuidePanel
                  contentId="connectwise-instructions-content"
                  expanded={connectWiseInstructionsExpanded}
                  onExpandedChange={setConnectWiseInstructionsExpanded}
                >
                  <ConnectWisePsaGuideInner />
                </CollapsibleSetupGuidePanel>
              )}
              {selectedPlatform?.toLowerCase() === "ninjaone" && (
                <CollapsibleSetupGuidePanel
                  contentId="ninjaone-instructions-content"
                  expanded={ninjaOneInstructionsExpanded}
                  onExpandedChange={setNinjaOneInstructionsExpanded}
                >
                  <NinjaOneRmmGuideInner />
                </CollapsibleSetupGuidePanel>
              )}
              {selectedPlatform?.toLowerCase() === "datto" && (
                <CollapsibleSetupGuidePanel
                  contentId="datto-instructions-content"
                  expanded={dattoInstructionsExpanded}
                  onExpandedChange={setDattoInstructionsExpanded}
                >
                  <DattoRmmGuideInner />
                </CollapsibleSetupGuidePanel>
              )}
              {selectedPlatform?.toLowerCase() === "sherweb" && (
                <CollapsibleSetupGuidePanel
                  contentId="sherweb-instructions-content"
                  expanded={sherwebInstructionsExpanded}
                  onExpandedChange={setSherwebInstructionsExpanded}
                >
                  <SherwebSetupGuideSteps />
                </CollapsibleSetupGuidePanel>
              )}
              {/* The by-hand guide, for the path Theo does not drive. */}
              {/*
                Both ways of setting Workspace up, side by side (Q164). A blocked tab is not
                `disabled`: a disabled tab takes no pointer events, and the tooltip saying why it
                is blocked is the whole point of it. It is dimmed, marked aria-disabled, and its
                click is refused instead.
              */}
              {gworkspaceTabsShown && (
                <Tabs
                  value={gworkspaceTab}
                  onChange={(_event, next: GWorkspaceTab) => {
                    if (gworkspaceTabBlocked(next)) return;
                    gworkspaceTabPicked.current = true;
                    setGworkspaceTab(next);
                  }}
                  sx={{ mb: 2, minHeight: 40, "& .MuiTabs-indicator": { backgroundColor: "#705D4A" } }}
                >
                  {([MANUAL_TAB, AUTO_TAB] as GWorkspaceTab[]).map((tab) => {
                    const why = gworkspaceTabBlocked(tab);
                    // describeChild, or the tooltip becomes the tab's accessible name and a
                    // blocked tab stops being findable by what it is called.
                    return (
                      <Tab
                        key={tab}
                        value={tab}
                        aria-disabled={Boolean(why)}
                        disableRipple={Boolean(why)}
                        label={
                          <Tooltip title={why} describeChild>
                            <span>{GWORKSPACE_TAB_LABEL[tab]}</span>
                          </Tooltip>
                        }
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          textTransform: "none",
                          fontSize: 14,
                          minHeight: 40,
                          opacity: why ? 0.45 : 1,
                          cursor: why ? "not-allowed" : "pointer",
                          "&.Mui-selected": { color: "#705D4A" },
                        }}
                      />
                    );
                  })}
                </Tabs>
              )}
              {gworkspaceManual && (
                <CollapsibleSetupGuidePanel
                  contentId="gworkspace-instructions-content"
                  expanded={gworkspaceInstructionsExpanded}
                  onExpandedChange={setGworkspaceInstructionsExpanded}
                >
                  <GWorkspaceSetupGuideSteps />
                </CollapsibleSetupGuidePanel>
              )}
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, fontFamily: 'var(--theo-font-sans)' }}>
                Configuration
              </Typography>
              {selectedApplication?.toLowerCase() === "m365" && (
                <Typography variant="body2" sx={{ mb: 2, color: "#666", fontFamily: 'var(--theo-font-sans)', fontSize: 13 }}>
                  Test Connection validates your Tenant ID, Client ID, and Client Secret only (Microsoft Graph
                  token)—no certificate or API permission checks.
                </Typography>
              )}
              {visibleConfigFields.map((field) =>
                field.type === "file" ? (
                  <Box key={field.key} sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontFamily: 'var(--theo-font-sans)',
                        fontSize: 14,
                        color: "rgba(0,0,0,0.6)",
                        mb: 1,
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Box
                      sx={{
                        p: 1.5,
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        backgroundColor: "#FFF",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          variant="outlined"
                          component="label"
                          disabled={onboardingRunning}
                          sx={{
                            fontFamily: 'var(--theo-font-sans)',
                            textTransform: "none",
                            borderColor: "#705D4A",
                            color: "#705D4A",
                            borderRadius: "6px",
                            px: 1.5,
                            minHeight: 40,
                            "&:hover": {
                              borderColor: "#604d3a",
                              background: "#f9f9f9",
                            },
                          }}
                        >
                          {field.uploadLabel || "Choose file"}
                          <input
                            type="file"
                            hidden
                            accept={field.accept || ".pfx"}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              handleFileChange(field.key, f ?? null, {
                                readMode: field.fileReadMode,
                              });
                              e.target.value = "";
                            }}
                          />
                        </Button>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                            ml: "auto",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "var(--theo-font-sans)",
                              fontSize: 13,
                              color: configValues[field.key] ? "#2E7D32" : "rgba(0,0,0,0.6)",
                              fontWeight: 500,
                            }}
                          >
                            {fileNames[field.key] ||
                              (configValues[field.key]
                                ? field.configuredLabel || "File configured"
                                : "No file selected")}
                          </Typography>
                          {configValues[field.key] && (
                            <Button
                              size="small"
                              onClick={() =>
                                handleFileChange(field.key, null, {
                                  readMode: field.fileReadMode,
                                })
                              }
                              sx={{
                                fontFamily: 'var(--theo-font-sans)',
                                textTransform: "none",
                                color: "#d32f2f",
                                minWidth: 0,
                                px: 0.5,
                              }}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    {field.helpText && (
                      <Typography
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          fontSize: 12,
                          color: "rgba(0,0,0,0.6)",
                          mt: 1,
                          lineHeight: 1.4,
                        }}
                      >
                        {field.helpText}
                      </Typography>
                    )}
                    {errors[field.key] && (
                      <Typography
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          fontSize: 12,
                          color: "#d32f2f",
                          mt: 1,
                        }}
                      >
                        {errors[field.key]}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box key={field.key} sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      disabled={onboardingRunning || ninjaOneRefreshTokenLoading || (selectedPlatform.toLowerCase() === "ninjaone" && (testing || loading))}
                      label={field.label}
                      type={field.type === "password" ? "password" : field.type === "email" ? "email" : "text"}
                      multiline={field.type === "textarea"}
                      rows={field.type === "textarea" ? 4 : 1}
                      value={configValues[field.key] || ""}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      onBlur={() => {
                        if (selectedPlatform?.toLowerCase() === "hudu" && field.key === "base_url") {
                          const normalized = normalizeHuduBaseUrl(configValues[field.key] || "");
                          if (normalized && normalized !== configValues[field.key]) {
                            handleFieldChange(field.key, normalized);
                          }
                        }
                      }}
                      error={!!errors[field.key]}
                      helperText={errors[field.key] || field.helpText}
                      placeholder={field.placeholder}
                      required={field.required}
                      sx={{
                        mb: selectedPlatform?.toLowerCase() === "ninjaone" && field.key === "refresh_token" ? 1 : 0,
                        fontFamily: 'var(--theo-font-sans)',
                        "& .MuiOutlinedInput-root": {
                          fontFamily: 'var(--theo-font-sans)',
                          "& fieldset": {
                            borderColor: "#E0E0E0",
                          },
                          "&:hover fieldset": {
                            borderColor: "#705D4A",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#705D4A",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          fontFamily: 'var(--theo-font-sans)',
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#705D4A",
                        },
                      }}
                    />
                    {selectedPlatform?.toLowerCase() === "ninjaone" && field.key === "refresh_token" ? (
                      <Button
                        variant="outlined"
                        onClick={handleStartNinjaOneSignIn}
                        disabled={
                          ninjaOneRefreshTokenLoading || testing || loading ||
                          !configValues.instance_url?.trim() ||
                          !configValues.client_id?.trim() ||
                          !configValues.client_secret?.trim()
                        }
                        sx={{ fontFamily: "var(--theo-font-sans)", textTransform: "none", borderColor: "#705D4A", color: "#705D4A" }}
                      >
                        {ninjaOneRefreshTokenLoading ? "Waiting for NinjaOne..." : "Get Refresh Token"}
                      </Button>
                    ) : null}
                  </Box>
                )
              )}
            </Box>
          )}

          {gworkspaceGuided && !onboardingFailedOpen && (
            <FormControlLabel
              sx={{ mt: -1, mb: 1, alignItems: "flex-start" }}
              control={
                <Checkbox
                  checked={onboardingAssignLicense}
                  onChange={(event) => setOnboardingAssignLicense(event.target.checked)}
                  // Locked exactly when [Start] is: the run in flight, or one that already exists, took its value.
                  disabled={startLocked}
                  inputProps={{ "aria-label": "Assign a Google Workspace license automatically" }}
                  sx={{ color: "#705D4A", "&.Mui-checked": { color: "#705D4A" }, pt: 0.25 }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 14 }}>Assign a Google Workspace license automatically</Typography>
                  <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
                    A paid edition with a free seat, if the tenant has one. Without a license, Gmail, Calendar, Chat and Drive actions will
                    not work for this account.
                  </Typography>
                </Box>
              }
            />
          )}
          {/*
            Mounted for both tabs and hidden on the by-hand one: its status poll is how the dialog
            learns a run exists at all, which is what moves the technician to the Auto tab (Q171).
            Unmounting it on the other tab would mean never finding out.
          */}
          {gworkspaceTabsShown && effectiveCustomerId && (
            <Box sx={{ display: gworkspaceGuided ? "contents" : "none" }}>
            <GWorkspaceOnboardingPanel
              customerId={effectiveCustomerId}
              applicationId="gworkspace"
              adminEmail={configValues[GWORKSPACE_ADMIN_EMAIL_KEY] || ""}
              assignLicense={onboardingAssignLicense}
              startSignal={onboardingStartSignal}
              manuallyVerified={onboardingFailedOpen && testSucceeded}
              onRunChange={handleOnboardingRunChange}
              onConfigurationSaved={() => {
                setOnboardingSavedConfig(true);
                // The integration now exists, so the board behind the dialog is out of date.
                onSuccess();
              }}
              onFinished={handleOnboardingFinished}
              onCancelled={() => setOnboardingActive(false)}
              onStartFailed={() => setOnboardingActive(false)}
            />
            </Box>
          )}
          {/*
            What to do about it, under the checklist that shows what happened: the steps a failed
            run did not do (Q97, Q133), which are the ones the JSON field above leads into when no
            key was saved.
          */}
          {onboardingFailedOpen && (
            <CollapsibleSetupGuidePanel
              contentId="gworkspace-instructions-content"
              expanded={gworkspaceInstructionsExpanded}
              onExpandedChange={setGworkspaceInstructionsExpanded}
            >
              <GWorkspaceManualStepInstructions steps={onboardingFailedSteps} heading="Finish the setup by hand" />
            </CollapsibleSetupGuidePanel>
          )}
          {testResult &&
            !m365SaveSuccess &&
            !(mode === "platform" && selectedPlatform?.toLowerCase() === "halo") &&
            !(mode === "platform" && selectedPlatform?.toLowerCase() === "datto") &&
            !(
              mode === "platform" &&
              selectedPlatform?.toLowerCase() === "itglue" &&
              itGlueAccessFeedback
            ) && (
            <Alert
              severity={
                testResult.success
                  ? testResult.has_warnings
                    ? "warning"
                    : "success"
                  : "error"
              }
              sx={{ mt: 2 }}
            >
              <Typography component="div" sx={{ whiteSpace: "pre-line" }}>
                {testResult.message}
              </Typography>
              {isGWorkspace && testResult.scope_checks?.length ? (
                <GWorkspaceOAuthScopeChecksResultsBox
                  checks={testResult.scope_checks}
                />
              ) : null}
              {testResult.checks?.length ? (
                <GWorkspaceConnectionChecksResultsBox
                  checks={testResult.checks}
                  title={isGWorkspace ? undefined : "Connection checks"}
                />
              ) : null}
            </Alert>
          )}
          {mode === "platform" && selectedPlatform?.toLowerCase() === "datto" && testResult && !m365SaveSuccess ? (
            <DattoAccessFeedbackAlert result={testResult} />
          ) : null}
          {mode === "platform" && selectedPlatform?.toLowerCase() === "halo" && haloPermissionFeedback && !m365SaveSuccess && (
            <HaloPermissionFeedbackAlert
              feedback={haloPermissionFeedback}
              connectionMessage={testResult?.message}
              onClose={() => setHaloPermissionFeedback(null)}
            />
          )}
          {mode === "platform" &&
            selectedPlatform?.toLowerCase() === "itglue" &&
            itGlueAccessFeedback &&
            !m365SaveSuccess && (
            <ItGlueAccessFeedbackAlert
              feedback={itGlueAccessFeedback}
              connectionMessage={testResult?.message}
              onClose={() => setItGlueAccessFeedback(null)}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, flexShrink: 0, borderTop: "1px solid #eee" }}>
        {m365SaveSuccess ? (
          <Button
            onClick={handleM365Done}
            variant="contained"
            sx={{
              backgroundColor: '#705d4a',
              '&:hover': { backgroundColor: '#5a4a3a' },
              fontFamily: 'var(--theo-font-sans)',
            }}
          >
            Done
          </Button>
        ) : (
          <>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          sx={{ fontFamily: 'var(--theo-font-sans)' }}
        >
          {/*
            A run in flight cannot be called off -- the jobs carry on in the background whatever
            this dialog does -- so the button says what it actually does: it closes the dialog.
          */}
          {onboardingRunning ? "Close" : "Cancel"}
        </Button>
        {configSchema.length > 0 && !isOnePassword && (
          <Button
            onClick={gworkspaceGuided && onboardingKeySaved ? handleTestSavedGWorkspace : handleTest}
            disabled={
              loading ||
              testing ||
              onboardingTesting ||
              ninjaOneRefreshTokenLoading ||
              (mode === "platform" && !selectedPlatformType) ||
              (mode === "application" && !selectedApplication) ||
              // Nothing to test until the run has saved a key or a failure has asked for one (Q137).
              (gworkspaceGuided && (onboardingActive || onboardingRunning || (!onboardingKeySaved && !gworkspaceJsonRequired)))
            }
            variant="outlined"
            sx={{
              fontFamily: 'var(--theo-font-sans)',
              borderColor: "#E0E0E0",
              color: "#161616",
              "&:hover": {
                borderColor: "#705D4A",
                backgroundColor: "transparent",
              },
            }}
          >
            {testing || onboardingTesting ? <CircularProgress size={20} /> : "Test"}
          </Button>
        )}
        <Button
          onClick={gworkspaceGuided && !onboardingFailedOpen ? handleStartOnboarding : handleSave}
          disabled={
            loading ||
            testing ||
            onboardingTesting ||
            ninjaOneRefreshTokenLoading ||
            configSchema.length === 0 ||
            (mode === "platform" && !selectedPlatformType) ||
            // [Start] is disabled once pressed and for good once a run exists (Q132) -- unless that
            // run only refused the address, which another address may fix (Q162); [Save] takes its
            // place after any other failure and, as everywhere, only opens after a green Test (Q131).
            (gworkspaceGuided && !onboardingFailedOpen ? startLocked : !testSucceeded && !isOnePassword)
          }
          variant="contained"
          sx={{
            backgroundColor: '#705d4a',
            '&:hover': { 
              backgroundColor: '#5a4a3a' 
            },
            '&:disabled': { 
              backgroundColor: '#ccc' 
            },
            fontFamily: 'var(--theo-font-sans)',
          }}
        >
          {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : gworkspaceGuided && !onboardingFailedOpen ? "Start" : "Save"}
        </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddIntegrationModal;
